import React, { useContext, useEffect, useRef, useState } from "react";
import * as _ from "lodash-es";
import OwLabel from "../../shared/OwLabel/OwLabel";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import { useLocation } from "react-router-dom";
import IBreadcrumbs from "../../../models/IBreadcrumbs";
import { NavLink } from "react-router-dom";
import OwButton from "../../shared/OwButton/OwButton";
import { FieldsetActions } from "../../shared/FieldsetActions/FieldsetActions";
import SupplierService from "../../../services/SupplierService";
import UIService from "../../../services/UIService";
import SocketService from "../../../services/SocketService";
import { ClickToCopy } from "../../shared/ClickToCopy/ClickToCopy";
import AccessTypesEnum from "../../../models/AccessTypesEnum";
import IOnGetSupplierUsageDTO from "../../../models/DTO/IOnGetSupplierUsageDTO";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import ScreenOptionsService from "@services/ScreenOptionsService";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import ViewAccessService from "@services/ViewAccessService";
import { useAppContextNonNull } from "@contexts/AppContext";
import { hasAccountUsersOverride } from "../accountUsersOverride";

type Props = {
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    showSupplierVendorSidebar: () => void;
    downloadCsv: () => void;
    uploadCsvModal: () => void;
    supplierUsage?: IOnGetSupplierUsageDTO;
};

export const AccountsHeader: React.FC<Props> = (props) => {
    const { userModel, supplierModel } = useAppContextNonNull();
    const location = useLocation();

    const getReadOnly = () => {
        let readOnly = false;

        if (userModel.supplierUserToken?.is_sales_rep) {
            if (!supplierModel.allow_sales_reps_create_accounts) {
                readOnly = true;
            }
        }

        if (userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.READ_ONLY) {
            readOnly = true;
        }

        if (!ScreenOptionsService.hasEditAccess(ScreenOptionsEnum.ACCOUNTS, userModel, supplierModel, ViewAccessService.UsersVendors)) {
            readOnly = true;
        }

        if (hasAccountUsersOverride(userModel, supplierModel)) {
            readOnly = false;
        }

        return readOnly;
    };

    const getAccountsUsersReadOnly = () => {
        let readOnly = false;

        if (userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.READ_ONLY) {
            readOnly = true;
        }

        if (
            !ScreenOptionsService.hasReadAccess(
                ScreenOptionsEnum.ACCOUNT_USERS,
                userModel,
                supplierModel,
                ViewAccessService.UsersVendorsUsers
            )
        ) {
            readOnly = true;
        }

        if (hasAccountUsersOverride(userModel, supplierModel)) {
            readOnly = false;
        }

        return readOnly;
    };

    const [vendorBtnClassName, setVendorBtnClassName] = useState("");
    const [vendorUserBtnClassName, setVendorUserBtnClassName] = useState("");
    const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>([]);
    const [activeStyle, setActiveStyle] = useState({ fontWeight: "bold", color: "#FFF", borderBottom: "1px solid white" });
    const [inactiveStyle, setInactiveStyle] = useState({ fontWeight: "bold", color: "#EEE" });
    const [vendorLinkTo, setVendorLinkTo] = useState(SupplierService.updateStringWithVendorOverride("/vendors", supplierModel));
    const [vendorUsersLinkTo, setVendorUsersLinkTo] = useState(
        SupplierService.updateStringWithVendorOverride("/vendors/users", supplierModel)
    );
    const [isAccountsReadOnly, setIsAccountsReadOnly] = useState(getReadOnly());
    const [isAccountsUsersReadOnly, setIsAccountsUsersReadOnly] = useState(getAccountsUsersReadOnly());

    const customersRef = useRef<HTMLAnchorElement | null>(null);
    const customerUsersRef = useRef<HTMLAnchorElement | null>(null);

    useEffect(() => {
        switch (location.pathname) {
            case "/vendors":
            case "/customers":
            case "/dealers":
            case "/retailers":
            case "/resellers":
                setVendorBtnClassName("active");
                break;

            case "/vendors/users":
            case "/customers/users":
            case "/dealers/users":
            case "/retailers/users":
            case "/resellers/users":
                setVendorUserBtnClassName("active");
                break;
        }

        if (supplierModel.vendor_name_override) {
            const nameOverride = supplierModel.vendor_name_override;

            // const _vendorLinkTo = vendorLinkTo.replace("vendor", nameOverride);
            // const _vendorUsersLinkTo = vendorUsersLinkTo.replace("vendor", nameOverride);
            //
            // setVendorLinkTo(_vendorLinkTo);
            // setVendorUsersLinkTo(_vendorUsersLinkTo);

            const _breadcrumbs: IBreadcrumbs[] = [
                { title: "vendors".replace("vendor", nameOverride), url: "/vendors".replace("vendor", nameOverride) },
            ];

            setBreadcrumbs(_breadcrumbs);
        }
    }, []);

    const getJoinCode = () => {
        let joinCode = "";

        if (!_.isNil(supplierModel) && supplierModel.guid) {
            joinCode = _.toUpper(supplierModel.guid.substr(0, 6));
        }

        let showJoinCode = false;

        if (userModel && userModel.isSupplierUser && supplierModel.has_customer_module) {
            showJoinCode = true;
        }

        if (supplierModel.disable_join_code) {
            showJoinCode = false;
        }

        if (!showJoinCode) {
            return null;
        }

        const joinCodeText = `${SocketService.getStaticSiteUrl()}/register?t=${joinCode}`;

        return (
            <div className={"d-flex justify-content-end"}>
                <div>
                    <div className={"text-center"}>
                        <div className={"ps-3 pe-3"} style={{ border: "1px solid white" }}>
                            <div>
                                <OwLabel title={"vendor_join_code"} supplierModel={supplierModel} isSupplierVendorOverride={true} />
                            </div>
                            <ClickToCopy
                                text={joinCode}
                                as={"h3"}
                                className={"faded-orange align-items-center d-flex justify-content-center mb-0"}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={"AccountsHeader"}>
            {/*<Breadcrumbs navItems={breadcrumbs} />*/}
            <OwTitle
                className="mt-4"
                title={"vendors"}
                leftIconName="fad fa-building-user"
                isSupplierVendorOverride={true}
                supplierModel={supplierModel}
            />
            <div className={"row d-flex mt-2"}>
                {props.supplierUsage ? (
                    <div className={"col-6"}>
                        <div className={"mt-4 d-inline-flex card " + (UIService.isMainViewLight() ? "" : "text-white bg-dark")}>
                            <div className="card-header">
                                <OwLabel title={"usage"} isBold={true} />
                            </div>
                            <div className="card-body">
                                <div className={"d-flex"}>
                                    {props.supplierUsage.customersCount}/{props.supplierUsage.allowedCustomersCount}
                                    <OwLabel
                                        className={"ms-2"}
                                        title={"unique_vendors_have_access"}
                                        isSupplierVendorOverride={true}
                                        supplierModel={supplierModel}
                                    />
                                </div>
                                <div>
                                    <OwLabel
                                        leftIconClass={"fas fa-question-circle"}
                                        className={"font-small text-muted"}
                                        title={"contact_support_more_customer_access"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}
                <div className={props.supplierUsage ? "col-6" : "col-12"}>{getJoinCode()}</div>
            </div>

            <div className={"row d-flex justify-content-between mt-4"}>
                <div className={"col d-flex align-items-center"}>
                    <div className={"btn-group"}>
                        <button className={"btn btn-primary " + vendorBtnClassName}>
                            <NavLink
                                ref={customersRef}
                                // exact={true}
                                style={inactiveStyle}
                                // activeStyle={activeStyle}
                                // activeClassName={"active"}
                                to={vendorLinkTo}>
                                <OwLabel
                                    noBottomMargin={true}
                                    title={"vendors"}
                                    className={"pointer"}
                                    supplierModel={supplierModel}
                                    isSupplierVendorOverride={true}
                                />
                            </NavLink>
                        </button>
                        {supplierModel.has_customer_module && !isAccountsUsersReadOnly ? (
                            <button className={"btn btn-primary " + vendorUserBtnClassName}>
                                <NavLink
                                    ref={customerUsersRef}
                                    // exact={true}
                                    style={inactiveStyle}
                                    // activeStyle={activeStyle}
                                    // activeClassName={"active"}
                                    to={vendorUsersLinkTo}>
                                    <OwLabel
                                        noBottomMargin={true}
                                        className={"pointer"}
                                        title={"vendor_users"}
                                        supplierModel={supplierModel}
                                        isSupplierVendorOverride={true}
                                    />
                                </NavLink>
                            </button>
                        ) : null}
                    </div>

                    {isAccountsReadOnly ? null : (
                        <div className={"ms-3"}>
                            <OwButton
                                title={"create"}
                                className={"btn-warning"}
                                fn={() => {
                                    props.showSupplierVendorSidebar();
                                }}
                                leftIconClass={"fas fa-plus"}
                            />
                        </div>
                    )}
                </div>

                {isAccountsReadOnly ? null : (
                    <div className={"col-auto"}>
                        <FieldsetActions
                            title={"csv"}
                            actions={[
                                { fn: props.downloadCsv, name: "download", leftIconClass: "fas fa-download" },
                                { fn: props.uploadCsvModal, name: "upload", btnClass: "btn-danger ms-2", leftIconClass: "fas fa-upload" },
                            ]}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
